import { cookieToken, getToken, logout } from '@/auth/store';
import { API_BASE } from '@/config';
import { T } from '@/i18n';
import { track } from '@/marketing/analytics';
import {
  beginWrite,
  ctx,
  endWrite,
  markSessionChanged,
  sessionChangedElsewhere,
  writesBlocked,
} from '@/shell/context';

export class ApiError extends Error {
  status: number;
  /** the parsed answer, for the few refusals that carry more than a message (a 409 naming the actual deduction, a 400 naming the clock) */
  body: unknown;
  /** the server's stable code (`error` in the answer): screens branch on this, never on the message */
  code: string | null;
  /** the route as a pattern (`/admin/shifts/:id`), for the error_shown event; never an id, a token or a query */
  route: string | null;
  constructor(
    status: number,
    message: string,
    body?: unknown,
    code: string | null = null,
    route: string | null = null,
  ) {
    super(message);
    this.status = status;
    this.body = body;
    this.code = code;
    this.route = route;
  }
}

/** The path with ids and tokens replaced, so an event never carries a person's or a link's identifier. */
export const routeOf = (path: string) =>
  (path.split('?')[0] ?? path).replace(/\/\d+(?=\/|$)/g, '/:id').replace(/\/[\w.-]{24,}(?=\/|$)/g, '/:token');

/** Shown when the server gave no sentence of its own, e.g. a proxy's HTML page. */
export const FALLBACK_MESSAGE = 'Noe gikk galt. Prøv igjen, eller kontakt support om det fortsetter.';

/** The answer belongs to a context that no longer exists; nothing reads it. */
export class StaleError extends ApiError {
  constructor() {
    super(499, 'Utdatert svar');
  }
}

/**
 * One fetch wrapper. Bearer token from the auth store; errors carry the API's
 * message. Every request is stamped with the context generation it started
 * under (TF-COMPANY-SWITCH): an answer arriving after the generation moved on
 * is discarded as StaleError, and a 401 logs out only when it belongs to the
 * current generation. A write is counted while in flight so a company switch
 * can wait for it; a write with no outcome (network failure) is recorded.
 * When another tab changed the session, nothing is sent from this one.
 */
export async function api<T>(path: string, init: RequestInit = {}, as: 'json' | 'blob' = 'json'): Promise<T> {
  if (sessionChangedElsewhere(getToken(), cookieToken())) {
    markSessionChanged();
    throw new ApiError(409, T('Økten ble endret i en annen fane'));
  }
  const method = (init.method ?? 'GET').toUpperCase();
  // the switch request and the login are the transition itself, not company writes
  const isWrite = method !== 'GET' && !path.endsWith('/tenants/switch-tenant') && path !== '/login';
  if (isWrite && writesBlocked()) throw new ApiError(423, T('Bytte av bedrift pågår'));
  const gen = ctx().gen;
  const token = getToken();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    // the error contract: `error` is a code, `message` a Norwegian sentence (backend/services/httpError.js)
    'X-Timefly-Errors': '2',
    ...(init.headers as Record<string, string> | undefined),
  };
  if (init.body && !(init.body instanceof FormData)) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;
  if (isWrite) beginWrite(gen);
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  } catch (e) {
    if (isWrite) endWrite(gen, false); // the server may or may not have applied it
    throw e;
  }
  if (isWrite) endWrite(gen, true); // counted against the generation that started it, never a newer one
  if (ctx().gen !== gen) throw new StaleError();
  // A 401 is a lost session, except on the login itself, where it is the answer to a wrong
  // password and carries the reason; logging out there showed "Ikke logget inn" instead
  if (res.status === 401 && path !== '/login') {
    logout();
    throw new ApiError(401, T('Økten er utløpt. Logg inn på nytt.'), null, 'session_expired');
  }
  // a file the server built (Excel, PDF) is bytes; a refusal is still JSON and read below
  if (as === 'blob' && res.ok) return (await res.blob()) as T;
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    // the sentence in `message` is what a person reads; the code in `error` is what screens compare
    const body = typeof data === 'object' && data ? (data as { error?: unknown; message?: unknown }) : null;
    const code = body && typeof body.error === 'string' ? body.error : null;
    const msg =
      body && body.message != null
        ? String(body.message)
        : body && body.error != null
          ? String(body.error)
          : T(FALLBACK_MESSAGE);
    throw new ApiError(res.status, msg, data, code, routeOf(path));
  }
  return data as T;
}

export const get = <T>(path: string) => api<T>(path);
export const post = <T>(path: string, body: unknown) =>
  api<T>(path, { method: 'POST', body: JSON.stringify(body) });
export const put = <T>(path: string, body: unknown) =>
  api<T>(path, { method: 'PUT', body: JSON.stringify(body) });
export const del = <T>(path: string) => api<T>(path, { method: 'DELETE' });

/** A file the server builds (Excel, PDF, #123), fetched with the session and saved under `name`. */
export async function saveFile(path: string, name: string): Promise<void> {
  const blob = await api<Blob>(path, {}, 'blob');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
}

/**
 * What a toast says when a write fails. The server's answer is already a sentence that says what
 * happened (docs/handbook/14-error-messages.md), so it is shown as it is; "Feilet: " before it read
 * twice. Anything else (the network, a bug in the page) keeps the word, so the person knows it failed.
 */
export function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    // what the person saw (#102): the route pattern and the status, never the sentence, which can name a person
    track('error_shown', { route: e.route, status: e.status });
    return e.message;
  }
  // fetch rejects with a TypeError when the request never reached the server; the browser's own
  // «Failed to fetch» is English and says nothing a person can act on (#272 B-28, M-14)
  if (e instanceof TypeError) return T('Fikk ikke kontakt med Timefly. Prøv igjen.');
  return `Feilet: ${e instanceof Error ? e.message : String(e)}`;
}
